import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/fieldops/lib/api";
import { invalidateAppSettings } from "@/fieldops/lib/useAppSettings";

/**
 * FT-2 §7 — Settings > Purchasing. Warehouse bin locations: the toggle (ships OFF) and the
 * editable bin list. A bin is recorded on the on-hand ledger when material is put away, so
 * the warehouse, the RA material status and the picking ticket all read one record.
 */
export default function PurchasingSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings/app").then((r) => setSettings(r.data)).catch(() => setSettings({})); }, []);

  const save = async (patch, label) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings/app", patch);
      setSettings(data);
      invalidateAppSettings();
      toast.success(label);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!settings) return null;
  const bins = settings.warehouse_bins || [];
  const addBin = () => {
    const label = draft.trim();
    if (!label) return;
    if (bins.some((b) => b.toLowerCase() === label.toLowerCase())) {
      toast.info(`${label} is already on the list`);
      return;
    }
    setDraft("");
    save({ warehouse_bins: [...bins, label] }, `Bin ${label} added`);
  };

  return (
    <div className="page-body" data-testid="purchasing-settings-page">
      <div className="mb-5">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">Purchasing</h1>
        <p className="text-sm text-slate-500 mt-1">
          Warehouse bin locations — where received material is put away, carried on the on-hand
          ledger and shown wherever the material is asked for.
        </p>
      </div>

      <section className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <label className="text-sm text-slate-700 flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1"
            data-testid="settings-warehouse-bin-locations"
            checked={!!settings.warehouse_bin_locations}
            onChange={(e) => save({ warehouse_bin_locations: e.target.checked },
                                   e.target.checked ? "Bin locations on" : "Bin locations off")}
          />
          <span>
            <b>Warehouse bin locations</b>
            <span className="block text-xs text-slate-500 mt-0.5">
              on = receiving a purchase order, breaking out a group order, a transfer or a confirmed
              return asks where in the warehouse it went. The bin then shows on the warehouse view,
              the RA material status and the picking ticket pull view. Off changes nothing that is
              already recorded.
            </span>
          </span>
        </label>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-slate-400" /> Bin list
        </h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          {bins.length} bin(s). Typed-in locations are always allowed — this list is what receiving
          suggests first.
        </p>
        <div className="flex items-center gap-2 mb-3">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addBin()}
            placeholder="e.g. Rack 4B, Mezz A2, Yard 3"
            className="h-9 max-w-xs text-sm"
            data-testid="settings-bin-input"
          />
          <Button size="sm" className="h-9" disabled={busy || !draft.trim()} onClick={addBin}
                  data-testid="settings-bin-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add bin
          </Button>
        </div>
        {bins.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="settings-bin-empty">No bins yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="settings-bin-list">
            {bins.map((b) => (
              <li key={b} className="flex items-center gap-2 py-1.5 text-sm text-slate-800"
                  data-testid={`settings-bin-row-${b.replace(/[^\w]+/g, "_")}`}>
                <span className="flex-1 font-mono">{b}</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => save({ warehouse_bins: bins.filter((x) => x !== b) }, `Bin ${b} removed`)}
                  className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-red-600"
                  aria-label={`Remove ${b}`}
                  data-testid={`settings-bin-remove-${b.replace(/[^\w]+/g, "_")}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
